"use client";

import { CoverImage } from "@/components/discover/CoverImage";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Expand, X } from "lucide-react";
import { useTranslations } from "next-intl";
import Image from "next/image";

interface EnlargeableCoverImageProps {
  src: string;
  alt: string;
  containerRatio: number;
  dimensions?: { width: number; height: number } | null;
  priority?: boolean;
  sizes?: string;
}

export function EnlargeableCoverImage({
  src,
  alt,
  containerRatio,
  dimensions,
  priority,
  sizes,
}: EnlargeableCoverImageProps) {
  const t = useTranslations("coverImage");

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          aria-label={t("enlarge")}
          className="group relative w-full h-full overflow-hidden rounded-sm cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <div className="absolute inset-0 transition-transform duration-300 ease-out group-hover:scale-110">
            <CoverImage
              src={src}
              alt={alt}
              containerRatio={containerRatio}
              dimensions={dimensions}
              priority={priority}
              sizes={sizes}
            />
          </div>
          <span
            aria-hidden
            className="inline-flex md:hidden items-center justify-center absolute bottom-3 right-3 size-10 rounded-full border bg-background shadow-xs"
          >
            <Expand className="size-4" />
          </span>
          <span
            aria-hidden
            className="hidden md:inline-flex items-center gap-2 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-9 px-4 py-2 text-sm font-medium rounded-full border bg-background shadow-xs opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-within:opacity-100"
          >
            <Expand className="size-4" />
            {t("enlarge")}
          </span>
          <span className="sr-only">{t("enlarge")}</span>
        </button>
      </DialogTrigger>
      <DialogContent
        showCloseButton={false}
        onCloseAutoFocus={(e) => e.preventDefault()}
        className="bg-background p-4 rounded-3xl border shadow-lg gap-0 sm:w-[825px]! sm:max-w-none! max-h-[95dvh]"
      >
        <DialogTitle className="sr-only">{t("dialogTitle")}</DialogTitle>
        <Image
          src={src}
          alt={alt}
          width={dimensions?.width ?? 825}
          height={dimensions?.height ?? 575}
          sizes="(min-width: 640px) 825px, 343px"
          className="block w-full h-auto max-h-[calc(95dvh-32px)] object-contain rounded-md"
        />
        <DialogClose asChild>
          <Button
            variant="outline"
            size="icon"
            className="absolute top-6 right-6 sm:top-8 sm:right-8"
          >
            <X className="size-4" />
            <span className="sr-only">{t("close")}</span>
          </Button>
        </DialogClose>
      </DialogContent>
    </Dialog>
  );
}
